import React, { useState, useEffect, useRef } from 'react';
import humanizeStatusMessage from '../utils/humanizeStatus';
import StepLayout from '../components/StepLayout';
import StepCaptchaViewer from '../components/StepCaptchaViewer';
import CodeEntryPanel from '../components/CodeEntryPanel';
import ApproveOnPhone from '../components/ApproveOnPhone';
import { api } from '../api';
import { connectSSE } from '../sse';
import { FINAL_DETAILS_STEP_NUMBER } from '../utils/emailChangeResume';

// 2FA v2 — same agent-driven mode detection as Step 5, but the success
// destination is email_change_success (or v3_done) and the next step is
// 12 (Final Details), skipping Step 11 (Session Cleanup) which the
// V3 email-change task handles inline.
export default function Step10EmailChange2FA({ step, totalSteps, sessionId, sessionData, updateData, onNext, goToStep, onInjectionDone, savedStep }) {
  // iter-47: unified advance helper. Branches on whether the seller
  // reached Step17 via the normal sequential flow (linear: code 2FA
  // arrived during email change, then continue to FinalDetails) OR
  // via useInjection from a later step (re-challenge after the seller
  // had already moved past Step17). In the injection case, App.jsx
  // recorded `savedStep` at the moment of injection. For email-change
  // challenges specifically, an early savedStep (login/2FA/etc.) is stale
  // once the code is accepted; resume to FinalDetails at minimum while
  // preserving PaymentDetails/later interruptions.
  // Operator-reported 2026-05-11: was on Payment Details, re-challenged
  // → submitted new code → got bounced "back a few steps" because every
  // advance path here was hard-coded to goToStep(19).
  const advance = () => {
    if (savedStep !== null && savedStep !== undefined && typeof onInjectionDone === 'function') {
      onInjectionDone(FINAL_DETAILS_STEP_NUMBER);
      return;
    }
    if (goToStep) {
      goToStep(FINAL_DETAILS_STEP_NUMBER);
      return;
    }
    onNext();
  };
  // iter-12: Step17 is the email-change-2FA injection page. The EPC
  // challenge during a LinkedIn email change is BY DEFINITION an email
  // code (sent to current primary inbox), never an authenticator app or
  // SMS. Default to 'email_code' instead of 'otp' so the seller-facing
  // copy + microcopy is correct on first paint, before any SSE event
  // arrives. Late-arriving needs_app_approval / needs_otp / needs_code
  // events still flip the mode if LinkedIn's UI surprises us.
  // iter-40: hard-default to 'email_code' regardless of sessionData.
  // Step07 (login 2FA) writes twoFAMode='otp' into sessionData; without
  // this hard reset that value bleeds across into Step17 and the seller
  // sees "Type in the 6-digit code from your authenticator app" while
  // LinkedIn is actually emailing them a code. SSE events fired during
  // the email-change challenge (needs_otp / needs_email_code /
  // needs_app_approval) still override correctly via handleEvent.
  const [twoFAMode, setTwoFAMode] = useState('email_code');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 'input' = waiting for code/approval; 'submitted' = code sent, awaiting V3.
  // 'submitted' is only meaningful for typed-code modes; for app_approval
  // the seller never clicks anything, so we stay in 'input' until v3 events
  // either succeed (advance) or fail (error).
  const [phase, setPhase] = useState('input');
  const [statusLines, setStatusLines] = useState([]);
  const [captchaActive, setCaptchaActive] = useState(false);
  const sseRef = useRef(null);
  const advancedRef = useRef(false);
  const codeInputRef = useRef(null);

  // iter-39: clear the visible code boxes so the seller can type
  // fresh. Called whenever a re-issued needs_*_code event arrives
  // after we've already submitted (= LinkedIn rejected the code).
  const clearCodeInput = () => {
    if (codeInputRef.current?.clear) codeInputRef.current.clear();
  };

  // Self-recover: if V3 already accepted the EPC code (current_step >= 11)
  // or fully verified the email change (current_step >= 12), skip the
  // 'Validating code' spinner entirely on mount. iter-41: bumped the
  // threshold from >=12 to >=11 (code accepted = seller done typing).
  // iter-43: CRITICAL gate — DO NOT advance if BE is currently awaiting
  // a new email-change 2FA code (awaiting_email_change_2fa === true).
  // Without this gate, a seller injected back to Step17 mid-flow
  // (LinkedIn re-challenged after they'd already passed Step19 / Step20)
  // would be instantly bounced BACK to Step19 by this effect, losing
  // the chance to submit the new code LinkedIn was waiting for.
  // Operator-reported 2026-05-11: was on Payment, injection navigated
  // to Step17, then this effect threw them straight back to
  // FinalDetails without showing the code input.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const body = await api.getStatus(sessionId);
        if (cancelled || advancedRef.current) return;
        const isAwaitingCode = body?.awaiting_email_change_2fa === true
          || body?.awaitingEmailChange2fa === true;
        if (isAwaitingCode) return;
        if (body?.currentStep && body.currentStep >= 11) {
          advancedRef.current = true;
          advance();
        }
      } catch (_) { /* transient */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, goToStep, onNext]);

  // iter-41: poll /status every 4s while the seller is on the
  // "Validating code" screen. ADVANCE AS SOON AS THE CODE IS ACCEPTED —
  // not when the full email change completes. BE semantics:
  //   current_step == 10 → EPC challenge presented, awaiting code
  //   current_step == 11 → code accepted, V3 past EPC (in post-EPC tail)
  //   current_step == 12 → email change fully verified on LinkedIn
  // iter-37 only advanced on emailChangeComplete === true (= step 12),
  // which can take 30s-15min after the code is typed in (depends on how
  // long inbox-reader takes to extract the verify URL). Sellers were
  // staring at the spinner for minutes after LinkedIn had already
  // accepted their code. Advance on current_step >= 11 instead, so
  // sellers can move onto FinalDetails while V3 finishes cleanup in
  // background. The post-EPC tail (verify-link click, make-primary,
  // remove-old-primary, sign-out-other-sessions) doesn't need seller
  // interaction.
  useEffect(() => {
    if (!sessionId || phase !== 'submitted') return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled || advancedRef.current) return;
      try {
        const body = await api.getStatus(sessionId);
        if (cancelled || advancedRef.current) return;
        // iter-43: don't advance while BE is awaiting a fresh code.
        // Covers two cases: (a) LinkedIn just rejected the submitted
        // code and re-presented the challenge (awaiting flag flipped
        // back to true between our submit and this poll); (b) the
        // seller was injected back into Step17 mid-flow from a later
        // page because BE needs another code — phase will be 'input'
        // there and this effect won't even run, but we belt-and-brace
        // here too.
        const isAwaitingCode = body?.awaiting_email_change_2fa === true
          || body?.awaitingEmailChange2fa === true;
        const pastEpc = (body?.currentStep && body.currentStep >= 11)
          || body?.emailChangeComplete === true;
        if (!isAwaitingCode && pastEpc) {
          advancedRef.current = true;
          if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
          advance();
          return;
        }
      } catch (_) { /* transient */ }
      timer = setTimeout(poll, 4000);
    };
    timer = setTimeout(poll, 4000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, phase, goToStep, onNext]);

  // Keep an SSE connection open from mount so app_approval mode can
  // detect the redirect without requiring a code submission first.
  useEffect(() => {
    if (!sessionId) return;
    sseRef.current = connectSSE(sessionId, handleEvent);
    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [sessionId]);

  // Persist the resolved mode.
  useEffect(() => {
    if (updateData) updateData({ twoFAMode });
  }, [twoFAMode, updateData]);

  function handleEvent(event) {
    const t = event.type;
    if (t === 'progress' || t === 'status') {
      const rawMessage = event.message || event.status
        || (event.action ? `${event.action}: ${event.why || ''}` : '');
      // iter-17: humanize and drop raw V3 traces.
      const friendly = humanizeStatusMessage(rawMessage);
      if (friendly !== null) {
        setStatusLines((prev) => {
          if (prev.length && prev[prev.length - 1] === friendly) return prev;
          return [...prev, friendly];
        });
      }
    } else if (t === 'needs_otp') {
      setTwoFAMode('otp');
      // iter-39: distinguish initial classification (no submit yet)
      // from re-issued challenge (submit happened, LinkedIn rejected).
      // Initial: clear any stale error so the input renders clean.
      // Re-issued: explicit rejection message + clear input boxes.
      const wasSubmitted = phase === 'submitted';
      setPhase('input');
      setCode('');
      if (wasSubmitted) {
        setError('Code rejected by LinkedIn. Please enter a fresh code.');
        clearCodeInput();
      } else {
        setError('');
      }
    } else if (t === 'needs_email_code') {
      setTwoFAMode('email_code');
      const wasSubmitted = phase === 'submitted';
      setPhase('input');
      setCode('');
      if (wasSubmitted) {
        setError('Code rejected by LinkedIn. Please enter a fresh code.');
        clearCodeInput();
      } else {
        setError('');
      }
    } else if (t === 'needs_app_approval') {
      setTwoFAMode('app_approval');
      setError('');
      setPhase('input');
    } else if (t === 'needs_code') {
      // Legacy event from un-redeployed BE — default to OTP unless we
      // already know it's app_approval.
      setTwoFAMode((prev) => (prev === 'app_approval' ? prev : 'otp'));
      setPhase('input');
      setCode('');
      setStatusLines((prev) => [
        ...prev,
        `Another code needed: ${event.context || ''}`,
      ]);
    } else if (t === 'needs_verify_url') {
      setStatusLines((prev) => [
        ...prev,
        `Reading verification link from inbox automatically: ${event.context || 'new email'}`,
      ]);
    } else if (t === 'inbox_reader_started') {
      setStatusLines((prev) => [
        ...prev,
        `Inbox reader started: ${event.email || ''}`,
      ]);
    } else if (t === 'verify_url_received') {
      // iter-37: this event ONLY fires after V3 successfully extracted
      // the verify link from the inbox — which only happens when
      // LinkedIn accepted the code and sent the verify email. So treat
      // it as an implicit code_accepted: advance the seller forward.
      // Operator-reported case: V3 had already extracted the link but
      // the FE was stuck waiting for a code_accepted event that never
      // arrived (V3 went directly into the verify-link flow).
      setStatusLines((prev) => [...prev, 'Verification link captured, continuing...']);
      if (advancedRef.current) return;
      advancedRef.current = true;
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
      setTimeout(() => {
        advance();
      }, 800);
    } else if (t === 'code_received') {
      // iter-15: code_received fires the moment V3's BLPOP wakes up
      // with the operator-relayed code, BEFORE LinkedIn validates it.
      // We don't advance here — that would risk skipping the seller
      // forward on a wrong code. Wait for `code_accepted` (V3 observed
      // the next iteration progressed past the challenge) instead.
      setStatusLines((prev) => [...prev, 'Code received, verifying with LinkedIn...']);
    } else if (t === 'code_accepted') {
      // iter-15: V3 confirmed LinkedIn accepted the previous code (the
      // next iteration moved past the challenge — not a re-block on
      // needs_*_code). The seller's hands-on work is done; V3's
      // post-EPC cleanup (verify-link click, make-primary, remove-old-
      // primary, sign-out-other-sessions) runs in background, can take
      // 30-90s. Skip Step18 Finalizing + Step19 SessionCleanup wait
      // screens; advance the seller to Step20 FinalDetails.
      setStatusLines((prev) => [...prev, 'Code accepted by LinkedIn']);
      if (advancedRef.current) return;
      advancedRef.current = true;
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      setTimeout(() => {
        // iter-26: FinalDetails is now at FE position 19 (was 20).
        advance();
      }, 800);
    } else if (t === 'code_rejected') {
      // iter-15: V3 detected LinkedIn re-presented the challenge after
      // the previous code was submitted. The next needs_*_code event
      // will trip iter-11's awaiting_2fa flag and useInjection will
      // pre-empt the seller back to Step17 with a fresh input. Don't
      // advance here — let useInjection handle the re-injection.
      //
      // B44 (2026-05-15): LinkedIn code TTLs are short (~5 min). Most
      // code_rejected events are EXPIRY, not typos — but LinkedIn doesn't
      // distinguish them on the DOM. Tell the seller to check inbox for a
      // FRESHER code rather than re-typing the old one. Rachel hit this
      // tonight: code 345615 was correct when issued but expired during
      // the 7-min recovery delay; she'd have retyped the same code without
      // this hint.
      setStatusLines((prev) => [
        ...prev,
        'Code didn\'t work. LinkedIn just sent a new one. Open your inbox, find the most recent code, and enter that. Older codes expire quickly.',
      ]);
    } else if (t === 'v3_done' || t === 'email_change_success' || t === 'completed') {
      setStatusLines((prev) => [...prev, 'Email change complete']);
      if (advancedRef.current) return;
      advancedRef.current = true;
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      // Skip wait screens (Finalizing 17, SessionCleanup 18) — V3 post-
      // EPC cleanup runs in background. iter-26: FinalDetails at FE 19.
      setTimeout(() => {
        advance();
      }, 800);
    } else if (t === 'v3_blocked' || t === 'error' || t === 'email_change_failed') {
      const reason = event.reason || event.message || event.error || '';
      let msg = reason || 'Email change failed. Please try again.';
      if (typeof reason === 'string' && reason.startsWith('app_approval_timeout')) {
        msg = 'We didn\'t receive an approval from your LinkedIn app. '
            + 'Please retry. Open the LinkedIn mobile app to receive '
            + 'the next push notification.';
      }
      setError(msg);
      setLoading(false);
    } else if (t === 'needs_captcha') {
      setStatusLines((prev) => [...prev, 'Security challenge. Please complete below']);
      setCaptchaActive(true);
    } else if (t === 'captcha_solved') {
      setCaptchaActive(false);
      setStatusLines((prev) => [...prev, 'Verification complete, continuing...']);
    } else if (t === 'captcha_failed') {
      setCaptchaActive(false);
      const reason = event.reason || 'Verification timed out';
      setError(`Verification failed: ${reason}`);
    }
  }

  const handleCodeComplete = (enteredCode) => {
    setCode(enteredCode);
  };

  const handleSubmit = async () => {
    if (code.length < 6) {
      setError('Please enter the full 6-digit code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.submitEmailChange2FA(sessionId, { code, method: 'otp' });
      setPhase('submitted');
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // iter-40: Resend Code removed (operator). LinkedIn drives retries
  // on its end; our resend endpoint doesn't actually re-trigger LI's
  // email send during email-change, so the button was misleading.

  const errorStyle = {
    color: 'var(--danger, #ef4444)',
    fontSize: 13,
    marginTop: 8,
    textAlign: 'center',
  };

  if (captchaActive) {
    return (
      <StepLayout
        title={null}
      >
        <StepCaptchaViewer
          sessionId={sessionId}
          onError={(reason) => {
            setCaptchaActive(false);
            setError(`Verification could not be loaded: ${reason}`);
          }}
        />
      </StepLayout>
    );
  }

  // Code submitted — V3 is processing. iter-37: stripped the status
  // panel ("Code accepted by LinkedIn" / "Reading verification link
  // from inbox..." etc.) per operator directive — internal plumbing
  // that doesn't help the seller. Just spinner + "Validating code"
  // + a friendly "this can take up to a minute" subline.
  if (phase === 'submitted' && twoFAMode !== 'app_approval') {
    return (
      <StepLayout title="Validating code">
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid #ffffff',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px',
          }} />
          <p style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            Validating code
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 24 }}>
            This can take up to a minute. Please don&rsquo;t close this window.
          </p>
          {error && <p style={errorStyle}>{error}</p>}
        </div>
      </StepLayout>
    );
  }

  // iter-21: title is now mode-aware and includes the inbox address when
  // we know it. Operator directive — "Confirm Email Change" was opaque.
  // Plain "Type in the 6 digit code sent to your <destination>" reads
  // immediately. The CodeEntryPanel headline + supporting copy are
  // suppressed (compactHeader prop) so the title stands alone.
  const inboxEmail = sessionData?.linkedinEmail || sessionData?.email;
  const inputTitle = twoFAMode === 'app_approval'
    ? 'Approve the email change on your phone'
    : (twoFAMode === 'email_code'
        ? (inboxEmail
            ? `Check your ${inboxEmail} inbox for the 6-digit code`
            : 'Type in the 6-digit code sent to your email')
        : 'Type in the 6-digit code from your authenticator app');

  return (
    <StepLayout title={inputTitle}>
      {twoFAMode === 'app_approval' ? (
        <ApproveOnPhone
          subtitle={"We'll continue automatically once LinkedIn confirms the email change."}
          error={error}
        />
      ) : (
        <CodeEntryPanel
          ref={codeInputRef}
          variant={twoFAMode === 'email_code' ? 'email_code' : 'otp'}
          inboxEmail={inboxEmail}
          compactHeader
          code={code}
          onCodeComplete={handleCodeComplete}
          onSubmit={handleSubmit}
          loading={loading}
          verifying={false}
          error={error}
          submitLabel="Submit"
        />
      )}
    </StepLayout>
  );
}
